import { T } from "./Translate";
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Toolbar,
  AppBar,
  TextField,
  Button,
  CircularProgress,
  Box,
  Alert,
  Snackbar,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Language as WebsiteIcon,
} from "../icons";
import {
  parsePhoneNumber,
  AsYouType,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Atras from "./BackButton";
import BottomToolbar from "./BottomToolbar";
import { pageContentSx } from "../pageLayout";
import { barColors } from "../theme";
import { saveContact, showsContact } from "../utils/contactPrivacy";
import { normalizeWebsite } from "../utils/safeUrl";

const countriesData = getCountries()
  .map((countryCode) => {
    try {
      return {
        code: countryCode,
        name: new Intl.DisplayNames(["es"], { type: "region" }).of(countryCode),
        phoneCode: `+${getCountryCallingCode(countryCode)}`,
        flag: countryCode
          .toUpperCase()
          .replace(/./g, (char) =>
            String.fromCodePoint(char.charCodeAt(0) + 127397)
          ),
      };
    } catch (e) {
      return null;
    }
  })
  .filter(Boolean)
  .sort((a, b) => a.name.localeCompare(b.name));

const EditProfile = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    displayName: "",
    fullname: "",
    phone: "",
    point: "",
    website: "",
  });

  const [selectedCountry, setSelectedCountry] = useState(
    countriesData.find((country) => country.code === "AR") || countriesData[0]
  );

  const [validations, setValidations] = useState({
    displayName: { error: false, message: "" },
    fullname: { error: false, message: "" },
    phone: { error: false, message: "" },
    point: { error: false, message: "" },
    website: { error: false, message: "" },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [hasChanges, setHasChanges] = useState(false);
  // Cuentas protegidas (ver PROTECTED_OWNER_EMAILS/isProtectedOwnerAccount en
  // firestore.rules) guardan el teléfono en users/{uid}/private/contact en
  // vez del doc público users/{uid}, para no exponerlo a cualquiera que lea
  // el perfil (ver match /private/{docId} en firestore.rules). Si ese doc
  // existe, hay que leer/guardar el teléfono ahí y no en el doc público.
  const hasPrivateContactRef = React.useRef(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);

          const privateContactRef = doc(db, "users", user.uid, "private", "contact");
          const privateContactSnap = await getDoc(privateContactRef).catch(() => null);
          const privateContactData = privateContactSnap?.exists() ? privateContactSnap.data() : null;
          if (docSnap.exists()) {
            const userData = docSnap.data();
            // Contacto en el doc privado salvo que el usuario eligió
            // mostrarlo (ver utils/contactPrivacy.js); las cuentas protegidas
            // que ya lo tenían en privado siguen así.
            hasPrivateContactRef.current =
              !showsContact(userData) || (!!privateContactData && userData.mostrarContacto !== true);
            const phoneValue = privateContactData?.phone || userData.phone;

            if (phoneValue) {
              // Precarga el teléfono siempre, aunque no se pueda inferir el
              // país (ej. si el número no matchea ningún code de
              // countriesData): antes, ese caso no llamaba a setFormData en
              // absoluto y el campo quedaba vacío.
              let formattedPhone = phoneValue;
              try {
                const phoneNumber = parsePhoneNumber(phoneValue);
                const country = countriesData.find(
                  (c) => c.code === phoneNumber.country
                );
                if (country) {
                  setSelectedCountry(country);
                  const formatter = new AsYouType(country.code);
                  formattedPhone = formatter.input(phoneValue) || phoneValue;
                }
              } catch (e) {
                console.error("Error parsing phone number:", e);
              }
              setFormData({
                displayName: userData.displayName || "",
                fullname: userData.fullname || "",
                phone: formattedPhone,
                point: userData.point || "",
                website: userData.website || "",
              });
            } else {
              setFormData({
                displayName: userData.displayName || "",
                fullname: userData.fullname || "",
                phone: "",
                point: userData.point || "",
                website: userData.website || "",
              });
            }
          }
        } catch (error) {
          console.error("Error loading user data:", error);
          setSnackbar({
            open: true,
            message: <T>Error al cargar los datos del usuario</T>,
            severity: "error",
          });
        }
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const formatPhoneNumber = (value, country) => {
    try {
      const formatter = new AsYouType(country.code);
      let formatted = formatter.input(value);

      if (!formatted.startsWith(country.phoneCode)) {
        formatted = formatted.replace(/^\D+/g, "");
        formatted = `${country.phoneCode} ${formatted}`;
      }

      return formatted;
    } catch (e) {
      return value;
    }
  };

  const handleChange = (field) => (event) => {
    let value = event.target.value;

    if (field === "phone") {
      value = formatPhoneNumber(value, selectedCountry);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setValidations((prev) => ({
      ...prev,
      [field]: { error: false, message: "" },
    }));

    setHasChanges(true);
  };

  const handleCountryChange = (event, newValue) => {
    if (newValue) {
      setSelectedCountry(newValue);
      if (formData.phone) {
        const phoneWithoutCode = formData.phone.replace(/^\+\d+\s*/, "");
        const newPhone = formatPhoneNumber(phoneWithoutCode, newValue);
        setFormData((prev) => ({
          ...prev,
          phone: newPhone,
        }));
      }
    }
  };

  const validateFullname = (fullname) => {
    if (!fullname || fullname.trim() === "") {
      return { error: true, message: <T>El nombre completo es requerido</T> };
    }
    if (fullname.trim().length < 5) {
      return {
        error: true,
        message: <T>El nombre completo debe tener al menos 5 caracteres</T>,
      };
    }
    if (!/^[A-Za-záéíóúñÁÉÍÓÚÑ\s]+$/.test(fullname)) {
      return {
        error: true,
        message: <T>El nombre solo puede contener letras y espacios</T>,
      };
    }
    return { error: false, message: "" };
  };

  const validateDisplayName = (displayName) => {
    if (!displayName || displayName.trim() === "") {
      return { error: true, message: <T>El nombre de usuario es requerido</T> };
    }
    if (displayName.trim().length < 3) {
      return {
        error: true,
        message: <T>El nombre de usuario debe tener al menos 3 caracteres</T>,
      };
    }
    if (!/^[A-Za-z0-9_]+$/.test(displayName)) {
      return {
        error: true,
        message: <T>Solo letras, números y guiones bajos</T>,
      };
    }
    return { error: false, message: "" };
  };

  const validatePhone = (phoneNumber, country) => {
    if (!phoneNumber.trim()) {
      return <T>El número de teléfono es requerido</T>;
    }

    try {
      const fullNumber = phoneNumber.startsWith("+")
        ? phoneNumber
        : `${country.phoneCode}${phoneNumber.replace(/^\D+/g, "")}`;

      const parsedNumber = parsePhoneNumber(fullNumber, country.code);

      if (!parsedNumber.isValid()) {
        return `Número inválido para ${country.name}`;
      }

      return "";
    } catch (error) {
      return <T>Formato de número inválido</T>;
    }
  };

  const validatePoint = (point) => {
    if (!point || point.trim() === "") {
      return { error: true, message: <T>La localidad es requerida</T> };
    }
    return { error: false, message: "" };
  };


  // Opcional: vacío está bien; si hay algo tiene que ser un sitio válido.
  const validateWebsite = (website) => {
    if (website.trim() && !normalizeWebsite(website)) {
      return { error: true, message: <T>Ingresá un sitio web válido, por ejemplo misitio.com</T> };
    }
    return { error: false, message: "" };
  };

  const validateForm = () => {
    const newValidations = {
      fullname: validateFullname(formData.fullname),
      displayName: validateDisplayName(formData.displayName),
      point: validatePoint(formData.point),
      website: validateWebsite(formData.website),
    };

    const phoneError = validatePhone(formData.phone, selectedCountry);
    newValidations.phone = {
      error: !!phoneError,
      message: phoneError,
    };

    setValidations(newValidations);
    return !Object.values(newValidations).some(
      (validation) => validation.error
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (!auth.currentUser) throw new Error("No user logged in");

      const userRef = doc(db, "users", auth.currentUser.uid);
      const fullNumber = formData.phone.startsWith("+")
        ? formData.phone
        : `${selectedCountry.phoneCode}${formData.phone.replace(/^\D+/g, "")}`;

      const updateData = {
        displayName: formData.displayName,
        fullname: formData.fullname,
        point: formData.point,
        website: normalizeWebsite(formData.website) || "",
      };

      await updateDoc(userRef, updateData);
      // El teléfono va al doc público o al privado según "Mostrar mi email y
      // teléfono" (ver utils/contactPrivacy.js).
      await saveContact(auth.currentUser.uid, { phone: fullNumber }, !hasPrivateContactRef.current);

      setSnackbar({
        open: true,
        message: <T>Perfil actualizado con éxito</T>,
        severity: "success",
      });

      setHasChanges(false);
      setTimeout(() => navigate(-1), 1500);
    } catch (error) {
      console.error("Error updating profile:", error);
      setSnackbar({
        open: true,
        message: <T>Error al actualizar el perfil</T>,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar position="fixed" sx={{ backgroundColor: barColors.background, color: barColors.text }}>
        <Toolbar
          sx={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              maxWidth: { xs: "100%", md: "980px" },
              mx: "auto",
            }}
          >
            <Box sx={{ cursor: "pointer" }}>
              <Atras color="inherit" />
            </Box>
            <Typography variant="h5" sx={{ ml: 2, color: barColors.text }}>
              <T>Editar perfil</T>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container sx={{ mt: 16, mb: 15 }}>
        <TextField
          fullWidth
          margin="normal"
          label={<T>Nombre de Usuario</T>}
          value={formData.displayName}
          onChange={handleChange("displayName")}
          error={validations.displayName.error}
          helperText={validations.displayName.message}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label={<T>Nombre Completo</T>}
          value={formData.fullname}
          onChange={handleChange("fullname")}
          error={validations.fullname.error}
          helperText={validations.fullname.message}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon />
              </InputAdornment>
            ),
          }}
        />

        <Autocomplete
          options={countriesData}
          value={selectedCountry}
          onChange={handleCountryChange}
          getOptionLabel={(option) =>
            `${option.flag} ${option.name} (${option.phoneCode})`
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={<T>País</T>}
              variant="outlined"
              margin="normal"
              fullWidth
            />
          )}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          disabled={isLoading}
        />

        <TextField
          fullWidth
          margin="normal"
          label={<T>Teléfono</T>}
          value={formData.phone}
          onChange={handleChange("phone")}
          error={validations.phone.error}
          helperText={
            validations.phone.message ||
            <><T>Formato para</T> {selectedCountry.name}: {selectedCountry.phoneCode}</>
          }
          disabled={isLoading}
          placeholder={`${selectedCountry.phoneCode} XXXXXXXXX`}
        />

        <TextField
          fullWidth
          margin="normal"
          label={<T>Localidad</T>}
          value={formData.point}
          onChange={handleChange("point")}
          error={validations.point.error}
          helperText={validations.point.message}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationIcon />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label={<T>Sitio web (opcional)</T>}
          value={formData.website}
          onChange={handleChange("website")}
          error={validations.website.error}
          helperText={validations.website.message}
          disabled={isLoading}
          placeholder="misitio.com"
          inputProps={{ inputMode: "url", autoCapitalize: "none", maxLength: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <WebsiteIcon />
              </InputAdornment>
            ),
          }}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !hasChanges}
          sx={{
            mt: 3,
            mb: 2,
            height: 42,
            textTransform: "none",
            fontSize: "1rem",
          }}
        >
          {isLoading ? <CircularProgress size={24} /> : <T>Guardar cambios</T>}
        </Button>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <BottomToolbar />
    </Container>
  );
};

export default EditProfile;